// Shared language detection utilities used by FileEditor (CodeMirror extension
// selection) and EditorStatusBar (display label). Keeping both in one place
// prevents the extension→language maps from drifting out of sync.

import type { Extension } from "@codemirror/state";
import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import { xml } from "@codemirror/lang-xml";
import { yaml } from "@codemirror/lang-yaml";
import { StreamLanguage } from "@codemirror/language";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { rust } from "@codemirror/legacy-modes/mode/rust";
import { go } from "@codemirror/legacy-modes/mode/go";
import { standardSQL } from "@codemirror/legacy-modes/mode/sql";
import { toml } from "@codemirror/legacy-modes/mode/toml";
import { swift } from "@codemirror/legacy-modes/mode/swift";
import { ruby } from "@codemirror/legacy-modes/mode/ruby";
import {
  c,
  cpp,
  java,
  kotlin,
  csharp,
  objectiveC,
  scala,
} from "@codemirror/legacy-modes/mode/clike";
import { dockerFile } from "@codemirror/legacy-modes/mode/dockerfile";
import { lua } from "@codemirror/legacy-modes/mode/lua";
import { perl } from "@codemirror/legacy-modes/mode/perl";
import { powerShell } from "@codemirror/legacy-modes/mode/powershell";
import { properties } from "@codemirror/legacy-modes/mode/properties";

// Returns the human-readable language label for the status bar.
export function languageLabelFor(path: string): string {
  const base = path.split("/").pop()?.toLowerCase() || "";
  if (base === "dockerfile") return "Dockerfile";
  if (base === "makefile") return "Makefile";

  const ext = path.split(".").pop()?.toLowerCase() || "";
  const map: Record<string, string> = {
    js: "JavaScript",
    jsx: "JSX",
    mjs: "JavaScript",
    cjs: "JavaScript",
    ts: "TypeScript",
    tsx: "TSX",
    css: "CSS",
    scss: "SCSS",
    less: "Less",
    html: "HTML",
    htm: "HTML",
    xml: "XML",
    svg: "SVG",
    md: "Markdown",
    markdown: "Markdown",
    py: "Python",
    json: "JSON",
    yml: "YAML",
    yaml: "YAML",
    sh: "Shell",
    bash: "Bash",
    zsh: "Zsh",
    rs: "Rust",
    go: "Go",
    sql: "SQL",
    toml: "TOML",
    swift: "Swift",
    rb: "Ruby",
    ruby: "Ruby",
    gemspec: "Ruby",
    c: "C",
    h: "C",
    cpp: "C++",
    cc: "C++",
    cxx: "C++",
    hpp: "C++",
    hh: "C++",
    java: "Java",
    kt: "Kotlin",
    kts: "Kotlin",
    cs: "C#",
    m: "Objective-C",
    mm: "Objective-C++",
    scala: "Scala",
    sc: "Scala",
    lua: "Lua",
    pl: "Perl",
    pm: "Perl",
    ps1: "PowerShell",
    psm1: "PowerShell",
    properties: "Properties",
    env: "Properties",
    ini: "INI",
    conf: "Conf",
    dockerfile: "Dockerfile",
  };
  return map[ext] || (ext ? ext.toUpperCase() : "Plain Text");
}

// Returns the CodeMirror Extension for syntax highlighting, or null for
// unsupported file types (xterm renders plain text in that case).
export function languageExtFor(path: string): Extension | null {
  const ext = path.split(".").pop()?.toLowerCase() || "";
  switch (ext) {
    case "js":
    case "jsx":
    case "mjs":
    case "cjs":
      return javascript({ jsx: ext === "jsx" });
    case "ts":
    case "tsx":
      return javascript({ jsx: ext === "tsx", typescript: true });
    case "css":
    case "scss":
    case "less":
      return css();
    case "html":
    case "htm":
      return html();
    case "xml":
    case "svg":
      return xml();
    case "md":
    case "markdown":
      return markdown();
    case "py":
      return python();
    case "json":
      return json();
    case "yml":
    case "yaml":
      return yaml();
    case "sh":
    case "bash":
    case "zsh":
      return StreamLanguage.define(shell);
    case "rs":
      return StreamLanguage.define(rust);
    case "go":
      return StreamLanguage.define(go);
    case "sql":
      return StreamLanguage.define(standardSQL);
    case "toml":
      return StreamLanguage.define(toml);
    case "swift":
      return StreamLanguage.define(swift);
    case "rb":
    case "ruby":
    case "gemspec":
      return StreamLanguage.define(ruby);
    case "c":
    case "h":
      return StreamLanguage.define(c);
    case "cpp":
    case "cc":
    case "cxx":
    case "hpp":
    case "hh":
      return StreamLanguage.define(cpp);
    case "java":
      return StreamLanguage.define(java);
    case "kt":
    case "kts":
      return StreamLanguage.define(kotlin);
    case "cs":
      return StreamLanguage.define(csharp);
    case "m":
    case "mm":
      return StreamLanguage.define(objectiveC);
    case "scala":
    case "sc":
      return StreamLanguage.define(scala);
    case "lua":
      return StreamLanguage.define(lua);
    case "pl":
    case "pm":
      return StreamLanguage.define(perl);
    case "ps1":
    case "psm1":
      return StreamLanguage.define(powerShell);
    case "properties":
    case "env":
    case "ini":
    case "conf":
      return StreamLanguage.define(properties);
    case "dockerfile":
      return StreamLanguage.define(dockerFile);
    default:
      if (path.split("/").pop()?.toLowerCase() === "dockerfile") {
        return StreamLanguage.define(dockerFile);
      }
      return null;
  }
}
